import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";

export function HistorySidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { runId: activeRunId } = useParams();

  const { data: runs, isLoading, error } = useQuery({
    queryKey: ["runs"],
    queryFn: () => api.listRuns(50),
    // Если есть pending — пуллим список каждые 5с, чтобы статус обновлялся
    // живьём (юзер увидит как pending перешло в completed без reload).
    refetchInterval: (q) => {
      const hasPending = q.state.data?.some((r) => r.status === "pending");
      return hasPending ? 5000 : false;
    },
  });

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery] = useDebouncedValue(searchInput, 200);

  const filteredRuns = useMemo(() => {
    if (!runs) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((r) => r.task_preview.toLowerCase().includes(q));
  }, [runs, searchQuery]);

  const deleteMutation = useMutation({
    mutationFn: (runId: string) => api.deleteRun(runId),
    onSuccess: (_resp, runId) => {
      queryClient.invalidateQueries({ queryKey: ["runs"] });
      // Если удалили активный — уйдём на главную
      if (runId === activeRunId) {
        navigate("/", { replace: true });
      }
      notifications.show({
        title: "Прогон удалён",
        message: "Запись убрана из истории",
        color: "gray",
        autoClose: 1500,
      });
    },
    onError: (err) => {
      notifications.show({
        title: "Не удалось удалить",
        message: String(err),
        color: "red",
      });
    },
  });

  const onDelete = (runId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm("Удалить этот прогон из истории?")) {
      deleteMutation.mutate(runId);
    }
  };

  return (
    <Stack gap="sm" h="100%">
      <Button
        leftSection={<IconPlus size={16} />}
        variant="filled"
        onClick={() => navigate("/")}
        fullWidth
      >
        Новый запрос
      </Button>

      <Group justify="space-between" align="center" mt="md">
        <Text fw={600} size="sm">
          История
        </Text>
        {runs && runs.length > 0 && (
          <Text size="xs" c="dimmed">
            {filteredRuns.length}/{runs.length}
          </Text>
        )}
      </Group>

      <TextInput
        placeholder="Поиск по тексту задачи…"
        size="xs"
        leftSection={<IconSearch size={14} />}
        value={searchInput}
        onChange={(e) => setSearchInput(e.currentTarget.value)}
      />

      <ScrollArea flex={1} type="hover">
        {isLoading && (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        )}
        {error && (
          <Text size="sm" c="red">
            Не удалось загрузить историю: {String(error)}
          </Text>
        )}
        {runs && runs.length === 0 && (
          <Text size="sm" c="dimmed">
            Пока пусто. Первый запрос создаст историю.
          </Text>
        )}
        {runs && runs.length > 0 && filteredRuns.length === 0 && (
          <Text size="sm" c="dimmed">
            Ничего не нашлось. Попробуй другие слова.
          </Text>
        )}
        {filteredRuns.length > 0 && (
          <Stack gap={4}>
            {filteredRuns.map((run) => {
              const date = new Date(run.created_at);
              const dateStr = date.toLocaleString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              });
              const isActive = run.id === activeRunId;
              return (
                <Group
                  key={run.id}
                  gap={4}
                  wrap="nowrap"
                  align="stretch"
                  className="history-row"
                >
                  <Button
                    component={Link}
                    to={`/runs/${run.id}`}
                    variant={isActive ? "light" : "subtle"}
                    color={isActive ? "indigo" : "gray"}
                    size="sm"
                    flex={1}
                    styles={{
                      root: { textAlign: "left", height: "auto", padding: "8px 10px" },
                      label: { whiteSpace: "normal", lineHeight: 1.3 },
                      inner: { justifyContent: "flex-start" },
                    }}
                  >
                    <Stack gap={2} w="100%">
                      <Group gap={6} wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          {dateStr}
                        </Text>
                        {run.status === "pending" && (
                          <Badge color="indigo" size="xs" variant="light">
                            идёт
                          </Badge>
                        )}
                        {run.status === "failed" && (
                          <Badge color="red" size="xs" variant="light">
                            упал
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" lineClamp={2}>
                        {run.task_preview}
                      </Text>
                    </Stack>
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    onClick={(e) => onDelete(run.id, e)}
                    aria-label="Удалить"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        )}
      </ScrollArea>
    </Stack>
  );
}
